import { useState, useMemo, useEffect, useRef, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../lib/LanguageContext';
import { PageSEO } from "../components/SEO";

// Lazy load the heavy Globe component
const Globe = lazy(() => import("react-globe.gl"));

// Well-spaced global cities — no overlapping, even coverage across continents
const GLOBE_CITIES = [
  { lat: 40.71, lng: -74.01 },   // New York
  { lat: -33.87, lng: 151.21 },  // Sydney
  { lat: 35.68, lng: 139.65 },   // Tokyo
  { lat: 51.51, lng: -0.13 },    // London
  { lat: -23.55, lng: -46.63 },  // Sao Paulo
  { lat: 28.61, lng: 77.21 },    // New Delhi
  { lat: -1.29, lng: 36.82 },    // Nairobi
  { lat: 55.76, lng: 37.62 },    // Moscow
  { lat: 34.05, lng: -118.24 },  // Los Angeles
  { lat: -34.60, lng: -58.38 },  // Buenos Aires
  { lat: 1.35, lng: 103.82 },    // Singapore
  { lat: 30.04, lng: 31.24 },    // Cairo
  { lat: 64.13, lng: -21.90 },   // Reykjavik
  { lat: -36.85, lng: 174.76 },  // Auckland
  { lat: 49.28, lng: -123.12 },  // Vancouver
  { lat: 25.20, lng: 55.27 },    // Dubai
];

export default function Login({ setUser }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    const stored = localStorage.getItem('sqawk_remember_me');
    return stored === null ? true : stored === 'true';
  });
  const [globeReady, setGlobeReady] = useState(false);
  const [shootingStars, setShootingStars] = useState([]);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const globeRef = useRef();
  const { t, language, setLanguage, languages } = useLanguage();
  const navigate = useNavigate();

  // Generate random twinkling stars once
  const twinkleStars = useMemo(() => 
    Array.from({ length: 45 }, (_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      size: 1.5 + Math.random() * 3,
      delay: Math.random() * 5,
      duration: 1.5 + Math.random() * 3,
      color: Math.random() > 0.85 
        ? (Math.random() > 0.5 ? 'rgba(16,185,129,0.9)' : 'rgba(34,211,238,0.9)') 
        : 'rgba(255,255,255,0.95)',
    })), []);

  // Determine API base at runtime based on current domain
  const API_BASE = useMemo(() => {
    const hostname = window.location.hostname;
    if (hostname === 'sqawk.app' || hostname === 'www.sqawk.app') {
      return ''; // Use relative URLs on production
    }
    return process.env.REACT_APP_BACKEND_URL || '';
  }, []);

  // Auto-rotate globe
  useEffect(() => {
    if (globeRef.current && globeReady) {
      const controls = globeRef.current.controls();
      if (controls) {
        controls.autoRotate = true;
        controls.autoRotateSpeed = 0.8;
        controls.enableZoom = false;
        controls.enablePan = false;
        controls.enableRotate = false;
      }
      globeRef.current.pointOfView({ altitude: 2.8 });
    }
  }, [globeReady]);

  // Generate shooting stars
  useEffect(() => {
    const createStar = () => {
      const star = {
        id: Date.now() + Math.random(),
        top: Math.random() * 60,
        right: Math.random() * 100,
        delay: 0,
      };
      setShootingStars(prev => [...prev.slice(-5), star]);
    };
    
    const interval = setInterval(createStar, 3000);
    createStar(); // Initial star
    
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Enter email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: email.trim().toLowerCase(), 
          password: password,
          remember_me: rememberMe
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError(t('invalidCredentials'));
          setLoading(false);
          return;
        }
        
        let errorMsg = 'Login failed. Please try again.';
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {}
        setError(errorMsg);
        setLoading(false);
        return;
      }

      const data = await response.json();
      
      if (data.session_token) {
        localStorage.setItem('sqawk_session', data.session_token);
        localStorage.setItem('sqawk_session_start', Date.now().toString());
        localStorage.setItem('sqawk_remember_me', rememberMe ? 'true' : 'false');
        // Set user immediately to avoid loading spinner
        if (data.user) setUser(data.user);
        // Smooth transition before navigating
        setLoginSuccess(true);
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 400);
        return;
      } else {
        setError('Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Unable to connect. Please try again.');
    }
    
    setLoading(false);
  };

  return (
    <div className={`min-h-screen bg-[#020617] overflow-hidden relative transition-all duration-500 ${loginSuccess ? 'scale-105 opacity-0' : 'scale-100 opacity-100'}`}>
      <PageSEO.Login />
      
      {/* Immersive CSS Starfield Background */}
      <div className="starfield-container">
        <div className="galaxy-band" />
        <div className="cosmic-dust" />
        <div className="starfield-layer starfield-back" />
        <div className="starfield-layer starfield-mid" />
        <div className="starfield-layer starfield-front" />
        <div className="nebula nebula-1" />
        <div className="nebula nebula-2" />
        <div className="nebula nebula-3" />
      </div>
      
      {/* Individual twinkling stars — each flickers independently */}
      <div className="fixed inset-0 z-[1] pointer-events-none">
        {twinkleStars.map(star => (
          <div
            key={star.id}
            className="twinkle-star"
            style={{
              position: 'absolute',
              top: `${star.top}%`,
              left: `${star.left}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              borderRadius: '50%',
              backgroundColor: star.color,
              boxShadow: `0 0 ${star.size * 2}px ${star.size * 0.5}px ${star.color}`,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Shooting Stars */}
      <div className="fixed inset-0 z-[2] pointer-events-none overflow-hidden">
        {shootingStars.map(star => (
          <div
            key={star.id}
            className="shooting-star absolute w-[100px] h-[2px] opacity-80"
            style={{
              background: 'linear-gradient(90deg, transparent, #10b981, #06b6d4)',
              top: `${star.top}%`,
              right: `${star.right}%`,
              animation: 'shootingStar 1.5s ease-out forwards',
            }}
          />
        ))}
      </div>

      {/* 3D Globe Background */}
      <div className="fixed inset-0 z-[1] globe-container" style={{ pointerEvents: 'none' }}>
        <Suspense fallback={<div className="w-full h-full" />}>
          <Globe
            ref={globeRef}
            globeImageUrl="https://unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
            bumpImageUrl="https://unpkg.com/three-globe/example/img/earth-topology.png"
            backgroundColor="rgba(0,0,0,0)"
            atmosphereColor="#10b981"
            atmosphereAltitude={0.35}
            pointsData={GLOBE_CITIES}
            pointLat="lat"
            pointLng="lng"
            pointColor={() => 'rgba(16, 185, 129, 0.9)'}
            pointAltitude={0.008}
            pointRadius={0.3}
            ringsData={GLOBE_CITIES}
            ringLat="lat"
            ringLng="lng"
            ringColor={() => t => `rgba(16, 185, 129, ${1 - t})`}
            ringMaxRadius={3}
            ringPropagationSpeed={2}
            ringRepeatPeriod={1400}
            onGlobeReady={() => setGlobeReady(true)}
            width={window.innerWidth}
            height={window.innerHeight}
            animateIn={false}
            rendererConfig={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
          />
        </Suspense>
      </div>

      {/* Subtle vignette for login form readability */}
      <div className="fixed top-0 left-0 w-full h-full bg-gradient-to-b from-transparent via-slate-950/30 to-slate-950/70 z-[3] pointer-events-none" />

      {/* Animated Flag Banner - Top Center */}
      <div className="fixed top-0 left-0 right-0 z-[100] flex justify-center gap-1.5 sm:gap-2.5 pt-4 pb-2 px-3"
        style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 1rem)' }}
      >
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            className={`text-xl sm:text-2xl hover:scale-125 transition-all duration-200 ${
              language === lang.code 
                ? 'scale-125 drop-shadow-lg ring-2 ring-emerald-500/50 rounded-full' 
                : 'opacity-70 hover:opacity-100'
            }`}
            aria-label={`Switch to ${lang.name}`}
            data-testid={`login-flag-${lang.code}`}
          >
            {lang.flag}
          </button>
        ))}
      </div>

      {/* Login Form */}
      <div className="relative z-[10] min-h-screen flex flex-col items-center justify-center p-4 pt-16">
        {/* Logo Above Card — floats down from above after globe loads */}
        <motion.img 
          src="https://customer-assets.emergentagent.com/job_09def4a9-eaab-440b-be7c-c94ecd8ec27b/artifacts/yhrzun8q_ChatGPT%20Image%20Jan%2027%2C%202026%2C%2007_59_06%20PM.png" 
          alt="sQawk Logo" 
          className="mb-4 object-contain drop-shadow-[0_0_30px_rgba(59,130,246,0.5)]"
          style={{ width: '220px', height: '220px' }}
          data-testid="login-logo"
          initial={{ opacity: 0, y: -60, scale: 0.7 }}
          animate={globeReady ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 1.0, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        />

        <motion.div 
          className="w-full max-w-[340px] p-6 sm:p-8 bg-slate-900/70 backdrop-blur-xl rounded-2xl border border-emerald-500/20 shadow-[0_20px_60px_rgba(16,185,129,0.25),0_8px_24px_rgba(6,182,212,0.12)]"
          initial={{ opacity: 0, y: 80 }}
          animate={globeReady ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Welcome Text */}
          <motion.div 
            className="text-center mb-6"
            initial={{ opacity: 0 }}
            animate={globeReady ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 1.0 }}
          >
            <h1 className="text-lg sm:text-xl font-semibold text-white mb-1">
              {t('welcomeBack')}
            </h1>
            <p className="text-slate-400 text-xs">
              {t('signInToContinue')}
            </p>
          </motion.div>

          <form onSubmit={handleSubmit} className="space-y-3" data-testid="login-form">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={globeReady ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
            >
            <input
              type="email"
              placeholder={t('email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 transition-all text-sm"
              autoComplete="email"
              data-testid="login-email"
            />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={globeReady ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
            >
            <input
              type="password"
              placeholder={t('password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 transition-all text-sm"
              autoComplete="current-password"
              data-testid="login-password"
            />
            </motion.div>
            
            {error && (
              <div className="p-2.5 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-xs text-center">
                {error}
              </div>
            )}

            <motion.div
              initial={{ opacity: 0 }}
              animate={globeReady ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 1.6 }}
            >
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me-toggle">
              <button
                type="button"
                role="switch"
                aria-checked={rememberMe}
                onClick={() => setRememberMe(!rememberMe)}
                className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${rememberMe ? 'bg-emerald-500' : 'bg-slate-600'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200 ${rememberMe ? 'translate-x-4' : ''}`} />
              </button>
              <span className="text-slate-400 text-xs">{t('rememberMe') || 'Remember me'}</span>
            </label>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={globeReady ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 1.8, ease: [0.22, 1, 0.36, 1] }}
            >
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-white font-semibold transition-all duration-300 shadow-lg shadow-emerald-500/30 text-sm"
              data-testid="login-submit"
            >
              {loading ? `${t('loading')}` : t('signIn')}
            </button>
            </motion.div>
          </form>

          <motion.div 
            className="mt-4 text-center"
            initial={{ opacity: 0 }}
            animate={globeReady ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 2.0 }}
          >
            <Link 
              to="/forgot-password" 
              className="text-slate-400 hover:text-emerald-400 text-xs transition-colors"
            >
              {t('forgotPassword')}
            </Link>
          </motion.div>

          <motion.div 
            className="mt-5 pt-4 border-t border-slate-700/50 text-center"
            initial={{ opacity: 0 }}
            animate={globeReady ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 2.1 }}
          >
            <p className="text-slate-400 text-xs">
              {t('dontHaveAccount')}{' '}
              <Link 
                to="/" 
                className="text-emerald-400 hover:text-emerald-300 font-semibold transition-colors"
              >
                {t('signUp')}
              </Link>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
